import React from "react";
import CrudManager from "../../common/CrudManager";
import { LayoutGrid, Edit2, Trash2 } from "lucide-react";

const SECTION_LABELS = {
  benefits: { label: "Benefits", color: "bg-blue-100 text-blue-700" },
  mission: { label: "Our Services", color: "bg-purple-100 text-purple-700" },
};

const ServiceCardManager = ({ onClose }) => {
  const fields = [
    {
      name: "section",
      label: "Section",
      type: "select",
      options: [
        { value: "benefits", label: "Benefits (Why Choose Us)" },
        { value: "mission", label: "Our Services (What We Do)" },
      ],
      required: true,
    },
    {
      name: "titleEn",
      label: "Title (English)",
      type: "text",
      placeholder: "e.g., Job Placement Support",
      required: true,
    },
    {
      name: "titleJa",
      label: "Title (Japanese)",
      type: "text",
      placeholder: "例：就職支援サービス",
      required: false,
    },
    {
      name: "descEn",
      label: "Description (English)",
      type: "textarea",
      placeholder: "Brief description of this card...",
      required: true,
    },
    {
      name: "descJa",
      label: "Description (Japanese)",
      type: "textarea",
      placeholder: "このカードの簡単な説明...",
      required: false,
    },
    {
      name: "order",
      label: "Display Order",
      type: "text",
      placeholder: "0",
      required: false,
    },
    {
      name: "isPublished",
      label: "Published",
      type: "select",
      options: [
        { value: "true", label: "Published" },
        { value: "false", label: "Draft" },
      ],
      required: false,
    },
  ];

  const formatCreateData = (formData) => ({
    section: formData.section,
    titleEn: formData.titleEn.trim(),
    titleJa: formData.titleJa?.trim() || undefined,
    descEn: formData.descEn.trim(),
    descJa: formData.descJa?.trim() || undefined,
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const formatUpdateData = (formData) => ({
    section: formData.section,
    titleEn: formData.titleEn.trim(),
    titleJa: formData.titleJa?.trim() || undefined,
    descEn: formData.descEn.trim(),
    descJa: formData.descJa?.trim() || undefined,
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const renderItem = (item, onEdit, onDelete) => {
    const sectionMeta = SECTION_LABELS[item.section] || {
      label: item.section,
      color: "bg-slate-100 text-slate-600",
    };

    return (
      <div className="flex items-start justify-between w-full group gap-4">
        <div className="flex items-start space-x-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-subidha-blue/5 rounded-2xl flex items-center justify-center border border-subidha-blue/10 group-hover:bg-subidha-blue transition-all duration-300 shrink-0">
            <LayoutGrid className="w-5 h-5 text-subidha-blue group-hover:text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="text-sm font-black text-slate-900 tracking-tight">
                {item.titleEn}
              </h3>
              <span
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${sectionMeta.color}`}
              >
                {sectionMeta.label}
              </span>
              <span
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  item.isPublished
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {item.isPublished ? "Published" : "Draft"}
              </span>
            </div>
            {item.titleJa && (
              <p className="text-[11px] text-slate-400 italic mb-1">
                {item.titleJa}
              </p>
            )}
            <p className="text-xs text-slate-500 truncate max-w-sm">
              {item.descEn}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1 shrink-0">
          <span className="text-[10px] font-black text-slate-300 mr-2">
            #{item.order ?? 0}
          </span>
          {onEdit && (
            <button
              onClick={() => onEdit(item)}
              className="p-2.5 text-slate-400 hover:text-subidha-blue hover:bg-subidha-blue/10 rounded-xl transition-all"
              title="Edit Card"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(item)}
              className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
              title="Delete Card"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <CrudManager
      title="Manage Service Cards"
      apiEndpoint="/service-cards"
      fields={fields}
      renderItem={renderItem}
      onClose={onClose}
      icon={LayoutGrid}
      emptyMessage="No service cards yet. Add your first card!"
      formatCreateData={formatCreateData}
      formatUpdateData={formatUpdateData}
    />
  );
};

export default ServiceCardManager;
